/**
 * The EIP-712 structs this client signs, as canonical typed-data.
 *
 * These definitions are the client half of a cryptographic contract: EIP-712
 * computes `typeHash = keccak256(encodeType(...))`, and `encodeType` emits each
 * member as `type ‖ " " ‖ name`, so **both the member names and the type names
 * are inside the hash**. One renamed field produces a valid signature over a
 * different digest, which the server rejects as `SIGNATURE_INVALID` with nothing
 * in the response explaining why.
 *
 * That failure mode already happened three times in this spec (docs/06 §F2,
 * §F7, §F8) and twice it was invisible to review because the type *strings*
 * matched while the value *encodings* differed. `eip712.golden.test.ts` pins the
 * digests so it cannot happen a fourth time silently.
 *
 * Field-level decisions and their evidence live in docs/06 §F7–F8.
 */

import type { TypedDataDomain } from 'viem'

/**
 * The domain is environment-specific: `verifyingContract` is the vault, and it
 * rotates. Read it from `GET /config` → `eip712.domain` at runtime and never
 * hardcode it — a client that hardcodes the vault signs for the wrong contract
 * the first time it moves (04 §1.5).
 *
 * The constant below exists **only** so the golden vectors are reproducible.
 */
export const GOLDEN_DOMAIN = {
  name: 'UpTik',
  version: '1',
  chainId: 42161,
  verifyingContract: '0x5c1f9e3a7b2d4068f1a3c5e7b9d1f3a5c7e9b1d3',
} as const satisfies TypedDataDomain

/**
 * `config.eip712.domain` → the EIP-712 domain, renaming two fields.
 *
 * **The wire is `snake_case` and EIP-712 is `camelCase`, and nothing enforces
 * the crossing.** `GET /config` serves
 * `{name, version, chain_id, verifying_contract}` — correct, because
 * `snake_case` on the wire is a project convention (CLAUDE.md) — while
 * `EIP712Domain` names its members `chainId` and `verifyingContract`, because
 * that is what goes into the hash.
 *
 * Handing the raw config object to a signer is therefore a **silent** failure:
 * viem ignores the two unrecognised keys and hashes a domain with no `chainId`
 * and no `verifyingContract`, producing a well-formed signature over a digest
 * nobody expected. It fails server-side as `SIGNATURE_INVALID`, with the
 * recovered address being some unrelated third address rather than anything
 * recognisable — which is exactly as undiagnosable as it sounds.
 *
 * Found by the mock's real signature verification during Phase 5 (docs/06
 * §F21l). It is the same shape as §F7 and §F8: the type string was right, the
 * payload was right, and the *encoding* of something adjacent was wrong.
 */
export function toEip712Domain(configDomain: unknown): TypedDataDomain {
  const raw = (configDomain ?? {}) as Record<string, unknown>
  const domain: TypedDataDomain = {}

  if (typeof raw.name === 'string') domain.name = raw.name
  if (typeof raw.version === 'string') domain.version = raw.version

  // Accept either spelling on the way in. The wire is `snake_case`; a server
  // that ever sends camelCase should not silently lose the field.
  const chainId = raw.chain_id ?? raw.chainId
  if (typeof chainId === 'number') domain.chainId = chainId
  else if (typeof chainId === 'string' && chainId !== '') domain.chainId = Number(chainId)

  const verifyingContract = raw.verifying_contract ?? raw.verifyingContract
  if (typeof verifyingContract === 'string') {
    domain.verifyingContract = verifyingContract as `0x${string}`
  }

  return domain
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Trade-only session key (D3). `scopes` has no withdraw bit, no transfer bit and
 * no key-registration bit, and the server has no code that would honour one.
 *
 * **Five members since docs/06 §F75.** The struct carried a spending cap and an
 * expiry until then, and the exchange checked both on every fill — so a match
 * the venue had already confirmed could be refused on chain because a key had
 * run past either. Both left the struct: the venue picks the key's lifetime
 * (`config.session_keys.key_ttl_ms`) and returns it as `expires_at_ms`, and the
 * exchange knows neither. Nothing this client signs carries a limit now.
 */
export const APPROVE_SESSION_KEY_TYPES = {
  ApproveSessionKey: [
    { name: 'account', type: 'address' },
    { name: 'session_key', type: 'address' },
    { name: 'label', type: 'string' },
    { name: 'scopes', type: 'uint8' },
    { name: 'nonce', type: 'uint64' },
  ],
} as const

/**
 * One order — **the settlement contract's struct, since §F26l.**
 *
 * This replaced a venue-specific struct in Phase 8 and the change is larger than
 * it looks. The old one carried `market_id`, `outcome`, `price_cents`, `size`,
 * `order_type`, `flags`, `nonce_ms` and `client_order_id`; this one carries
 * eleven members that a Solidity contract can verify. The reason is simply that
 * **the contract is now what verifies the signature**, so the struct is its
 * struct or it is nothing (`docs/04` §3.6b.2a). That held until `docs/06` §F94:
 * since then the venue verifies every order on arrival and keeps it as signed,
 * and no contract reads an order at all. The struct did not change, so nothing
 * this client signs moved.
 *
 * Four consequences are worth stating, because none of them is obvious from the
 * member list and all four were true the moment the struct changed:
 *
 * 1. **The price is a ratio, not a number.** `makerAmount / takerAmount` is the
 *    implied price; `price_cents` has no member. `docs/04` §7.4's integer
 *    arithmetic carries over exactly — `size_step` is `0.010000`, so
 *    `size_µ × cents` is always divisible by 100 and the conversion is *exact*,
 *    with no rounding rule to get wrong. That is luck the spec had already
 *    bought for a different reason.
 * 2. **`time_in_force`, `post_only`, `reduce_only` and `order_type` leave the
 *    digest.** A contract has no concept of IOC or post-only, so those become
 *    operator-trusted, exactly as they are at Polymarket. They stay in the
 *    request body and the venue still honours them; what changed is that a
 *    dishonest venue could no longer be *caught* by the signature. Recorded in
 *    register §F27c — it is a consequence of §F26l, not a new decision.
 * 3. **`client_order_id` and `nonce_ms` leave the digest but not the system.**
 *    Invariant 15's three mechanisms are unaffected: they are HTTP-level, and
 *    `salt` now carries their identity into the digest — see `wallet/order.ts`.
 * 4. **`expiration` is in the digest and not in the body.** Every verifier —
 *    the venue, the mock, a market maker's own check — rebuilds it as
 *    `market.expiry_at_ms + config.eip712.order_expiration_grace_ms`, so that is
 *    the one value a client may sign (`wallet/order.ts`, docs/06 §F75).
 *
 * The member names are the contract's `camelCase`, not this project's wire
 * `snake_case`, because they are inside the type hash and the contract compiles
 * them. `scripts/check-typehash-agreement.mjs` asserts all three spellings —
 * Solidity, this file, and `contracts/ctf-exchange.ts` — still agree.
 */
export const ORDER_TYPES = {
  Order: [
    { name: 'salt', type: 'uint256' },
    { name: 'maker', type: 'address' },
    { name: 'signer', type: 'address' },
    { name: 'taker', type: 'address' },
    { name: 'tokenId', type: 'uint256' },
    { name: 'makerAmount', type: 'uint256' },
    { name: 'takerAmount', type: 'uint256' },
    { name: 'expiration', type: 'uint256' },
    { name: 'feeRateBps', type: 'uint256' },
    { name: 'side', type: 'uint8' },
    { name: 'signatureType', type: 'uint8' },
  ],
} as const

/**
 * `signatureType`. **Inside the digest**, so these values are load-bearing.
 *
 * `EOA` requires `signer == maker`; `SESSION_KEY` makes the contract look the
 * signer up in its `key -> owner` registry and check the owner and the scope —
 * nothing else, since §F75 (`contracts/src/mixins/SessionKeys.sol`). This client always signs with a
 * session key, so it always sends `SESSION_KEY` — but the enum carries both
 * because a root-wallet signature is a legitimate order the contract accepts.
 */
export const SIGNATURE_TYPE_ENCODING = { EOA: 0, SESSION_KEY: 1 } as const

/**
 * "Withdraw" — gasless funding's withdrawal (`backend/GASLESS-PLAN.md`).
 *
 * **Not the struct §F28k deleted.** That one authorised the venue to send
 * money out of a vault it held; there is no vault, and this authorises nothing
 * of the kind. It asks USDU — from the user's own wallet's point of view — to
 * set the user's own money aside for the user's own unwrap, exactly as
 * `requestUnwrap` would, and the venue only sends the request and pays its gas
 * (`POST /chain/withdraw`). Signed under USDU's own domain,
 * `config.eip712.withdraw_domain` (`UpTik`, version `1`, the USDU contract).
 *
 * `validBefore` is chain **seconds** — the request body carries it times 1000 as
 * `valid_before_ms` — and `nonce` is 32 random bytes, used once.
 * `session-key.test.ts` still asserts no session-key scope can withdraw: this is
 * signed by the wallet, never by a session key.
 */
export const WITHDRAW_TYPES = {
  Withdraw: [
    { name: 'owner', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'validBefore', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' },
  ],
} as const

/**
 * "Add funds" — EIP-3009's `ReceiveWithAuthorization`, as Circle's USDC
 * verifies it. **Not this venue's struct**: the members are EIP-3009's spelling
 * exactly, and the domain is USDC's own (`config.chain.backing_eip712`,
 * `backing_address`). `to` is always USDU (`chain.collateral_address`): USDC
 * lets only the named payee redeem the authorisation, and USDU credits `from`.
 */
export const RECEIVE_WITH_AUTHORIZATION_TYPES = {
  ReceiveWithAuthorization: [
    { name: 'from', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'validAfter', type: 'uint256' },
    { name: 'validBefore', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' },
  ],
} as const

/** A `Withdraw` ready for a wallet's `signTypedData`. */
export function withdrawTypedData(args: {
  /** `config.eip712.withdraw_domain`, as served — renamed here (§F21l). */
  domain: unknown
  owner: `0x${string}`
  amountMicro: bigint
  validBeforeS: bigint
  nonce: `0x${string}`
}) {
  return {
    domain: toEip712Domain(args.domain),
    types: WITHDRAW_TYPES,
    primaryType: 'Withdraw' as const,
    message: {
      owner: args.owner,
      amount: args.amountMicro,
      validBefore: args.validBeforeS,
      nonce: args.nonce,
    },
  }
}

/** A `ReceiveWithAuthorization` naming USDU as payee, ready for `signTypedData`. */
export function receiveWithAuthorizationTypedData(args: {
  /** USDC's domain: `backing_eip712.name`/`version`, `chain_id`, `backing_address`. */
  domain: TypedDataDomain
  from: `0x${string}`
  usdu: `0x${string}`
  valueMicro: bigint
  validBeforeS: bigint
  nonce: `0x${string}`
}) {
  return {
    domain: args.domain,
    types: RECEIVE_WITH_AUTHORIZATION_TYPES,
    primaryType: 'ReceiveWithAuthorization' as const,
    message: {
      from: args.from,
      to: args.usdu,
      value: args.valueMicro,
      validAfter: 0n,
      validBefore: args.validBeforeS,
      nonce: args.nonce,
    },
  }
}

// ---------------------------------------------------------------------------
// Encodings
// ---------------------------------------------------------------------------

/**
 * `0 = BUY, 1 = SELL`. **Inside the digest**, as the struct's `side`.
 *
 * `outcome` is not fused into it and never was (§2.5) — but since §F26l the
 * outcome is not a struct member at all: `tokenId` addresses the market and the
 * side of it at once. `OUTCOME_ENCODING` was deleted with the old struct rather
 * than left behind, because a numeric outcome encoding that is no longer inside
 * any signature is exactly the sort of constant that gets wired back into one.
 */
export const SIDE_ENCODING = { BUY: 0, SELL: 1 } as const

/**
 * The **request body's** vocabulary, not the digest's.
 *
 * `order_type` and `time_in_force` left the signed struct in §F26l — a contract
 * has no concept of IOC or post-only, so the venue honours them on trust
 * (§F27c). These constants survive only as the source of the union types
 * `OrderKind` and `TimeInForce`; nothing hashes them.
 *
 * `encodeOrderFlags`, `FLAG_POST_ONLY`, `FLAG_REDUCE_ONLY` and `TIF_SHIFT` were
 * deleted for the same reason as `OUTCOME_ENCODING`. The bit-packing they did
 * has no consumer now, and a flags encoder sitting next to a typed-data
 * definition reads like part of it.
 */
export const ORDER_TYPE_ENCODING = { LIMIT: 0, MARKET: 1 } as const
/** Value 1 (`GTD`) is reserved and unused — §12 cut GTD from v1. */
export const TIF_ENCODING = { GTC: 0, IOC: 2, FOK: 3 } as const

/** `scopes` bitmask. `3` is the only value accepted in v1. */
export const SCOPE_PLACE_ORDER = 0x1
export const SCOPE_CANCEL_ORDER = 0x2
export const SCOPES_V1 = SCOPE_PLACE_ORDER | SCOPE_CANCEL_ORDER
